// @flow
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '@shared/Modal/Modal';
import Button, { buttonConstants } from '@shared/Button';
import PeopleExportService from '@groupClass/services/PeopleExportService';

type ExportType = 'beneficiaries' | 'participants';

type ExportConfig = {
  modalTitleKey: string,
  modalTextKey: string,
  fetchExport: () => Promise<boolean | null>,
};

type Props = {
  isActiveModalExport: boolean,
  toggleModalExport: () => void,
  exportType: ExportType,
};

const EXPORT_CONFIG: { [key: ExportType]: ExportConfig } = {
  beneficiaries: {
    modalTitleKey: 'beneficiaries.beneficiaryExport',
    modalTextKey: 'beneficiaries.modalBeneficiaryExportText',
    fetchExport: PeopleExportService.FetchBeneficiariesExport,
  },
  participants: {
    modalTitleKey: 'participants.participantExport',
    modalTextKey: 'participants.modalParticipantExportText',
    fetchExport: PeopleExportService.FetchParticipantsExport,
  },
};

const ModalPeopleExport = (props: Props): React$Node => {
  const {
    isActiveModalExport,
    toggleModalExport,
    exportType,
  } = props;
  const { t, i18n: { language } } = useTranslation();
  const { modalTitleKey, modalTextKey, fetchExport } = EXPORT_CONFIG[exportType];

  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback((): void => {
    setIsLoading(true);
    fetchExport()
      .then(toggleModalExport)
      .finally(() => setIsLoading(false));
  }, [fetchExport, toggleModalExport]);

  const footerModalExport: React$Node = useMemo((): React$Node => (
    <footer className="modal-export-footer">
      <Button
        type={ buttonConstants.PRIMARY }
        className="button-modal"
        onClick={ handleSubmit }
        disabled={ isLoading }
      >
        { isLoading ? t('common.loading') : t('common.validate') }
      </Button>
    </footer>
  ), [handleSubmit, language, isLoading]);

  return (
    <Modal
      isActive={ isActiveModalExport }
      isClipped
      onClose={ toggleModalExport }
      title={ t(modalTitleKey) }
      footer={ footerModalExport }
      modalContentClassName="modal-export-card"
    >
      <div className="modal-export-card">
        <p className="modal-export-text">{ t(modalTextKey) }</p>
        <div id="date-popup"/>
      </div>
    </Modal>
  );
};

export default ModalPeopleExport;
